/**
 * WHICH NUMBER GOES ON THE CHIP — one function, because there were three copies.
 *
 * ⭐ WHY IT EXISTS AT ALL (Pete, 2026-08-27): *"Appointment ID we create in the
 * system really doesn't help us much on any of the sliders, appointments, etc.
 * Its really the PO Number, ContainerID, Reference Number, Trailer Number."*
 *
 * A00000122 is OUR number. It is generated at booking, it appears on nothing the
 * driver or the warehouse is holding, and it was winning the chip's second line
 * on every load that had no PO — which is most containerised freight. The board
 * was showing a number nobody could match to a document.
 *
 * ⛔ THIS IS A FALLBACK CHAIN AND THAT IS DELIBERATE HERE, unlike the hover card.
 * The chip's second line is ONE truncated line at 10px sitting under the carrier;
 * three identifiers joined together would truncate to the first one and a half.
 * ⭐ So the chain picks the BEST single identifier, and the hover card — one
 * mouse-move away — shows every identifier the load carries, in full.
 *
 * ⛔ THE ORDER IS THE DRIVER'S ORDER, NOT OURS: PO, then container, then
 * reference, then the WMS document, and our own appointment number LAST — where
 * it is a last resort rather than a default.
 *
 * ⚠️ EVERY IDENTIFIER IS LABELLED EXCEPT THE LAST TWO. 'PO 88213' and 'Cont
 * MSCU5232930' say what they are; a bare number on a chip is a number the reader
 * has to guess at. `documentNumber` and `appointmentNumber` stay bare because
 * they carry their own recognisable shape.
 *
 * ⚠️ TRAILER NUMBER IS ABSENT ON PURPOSE. `AppointmentDto` does not carry it
 * (#277) — only the driver check-in back-fill writes one — so there is nothing
 * here to read. It joins the chain when the DTO carries it, not before.
 */
import type { AppointmentDto } from '@/api/types';

/** Blank, whitespace and null all mean "the load does not have one". */
function clean(v: string | null | undefined): string | null {
    const s = (v ?? '').trim();
    return s.length ? s : null;
}

/**
 * The one identifier worth the chip's single line, already labelled.
 *
 * ⛔ NEVER RETURNS EMPTY. A chip whose second line vanishes changes height and
 * makes the board jump; `appointmentNumber` is NOT NULL, so the chain terminates.
 */
export function loadIdentifier(a: AppointmentDto): string {
    const po = clean(a.poNumber);
    if (po) return `PO ${po}`;

    /**
     * ⭐ THE CONTAINER OUTRANKS THE REFERENCE, and it outranks everything except
     * the PO. On containerised freight it is the number printed on the pick
     * ticket in the driver's hand and on the seal on the door — the one string
     * that ties the truck at the gate to the paperwork on the desk.
     */
    const container = clean(a.containerNumber);
    if (container) return `Cont ${container}`;

    const reference = clean(a.referenceNumber);
    if (reference) return `Ref ${reference}`;

    return clean(a.documentNumber) ?? a.appointmentNumber;
}

/**
 * The chip's whole second line: the identifier, plus the pallet count.
 *
 * ⚠️ OUTBOUND WITH A CONSIGNEE STILL LEADS WITH THE CONSIGNEE. On a shipment the
 * question the board is asked is "who is this going to", and that has not
 * changed — this only fixes what is shown when there is no consignee to name.
 */
export function loadSubLine(a: AppointmentDto): string {
    if (a.direction === 'BLOCK') return 'Out of service';
    const base =
        a.direction === 'OUTBOUND' && a.consignees && a.consignees.length > 0
            ? (a.consignees.find((c) => c.isPrimary)?.consigneeCode ?? a.consignees[0].consigneeCode)
            : loadIdentifier(a);
    const plt = a.palletCount != null ? ` · ${a.palletCount} plt` : '';
    return `${base}${plt}`;
}
